import { Controller } from '@hotwired/stimulus'
import { showNotification } from '../javascript/flash-notifications'

export default class extends Controller {
  static targets = [
    'error',
    'loader',
    'form',
    'lockStatus',
    'lockOption',
    'permanentUnlockOption',
    'temporaryUnlockOption',
    'returnCodeOption',
    'operatingTime',
    'unlockCodes',
    'refetchButton',
  ]

  static values = {
    serialNumber: String,
    state: String,
    status: String,
    operatingTime: String,
  }

  connect() {
    this.fetchStatus()
  }

  disconnect() {
    if (this.statusFetchTimer) {
      clearTimeout(this.statusFetchTimer)
    }
  }

  toggleFormDisabled(disabled = true) {
    this.formTarget.classList.toggle('is-disabled', disabled)
  }

  beforeSend({ target }) {
    this.toggleFormDisabled(true)
    this.clearUnlockCodes()

    let buttonTarget = target

    if (buttonTarget.tagName === 'FORM') {
      buttonTarget = buttonTarget.querySelector('input[type="submit"]')
    }

    if (!buttonTarget) {
      console.error('Button target not found')
      return
    }

    buttonTarget.dataset.originalText = buttonTarget.textContent
    buttonTarget.textContent = 'In progress...'

    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('is-hidden')
  }

  afterSend({ target }) {
    this.toggleFormDisabled(false)

    let buttonTarget = target

    if (buttonTarget.tagName === 'FORM') {
      buttonTarget = buttonTarget.querySelector('input[type="submit"]')
    }

    if (!buttonTarget) {
      console.error('Button target not found')
      return
    }

    buttonTarget.removeAttribute('disabled')
    buttonTarget.classList.remove('is-disabled')

    buttonTarget.textContent = buttonTarget.dataset.originalText
  }

  onSendError(e) {
    const [data, ,] = e.detail

    if (data.response || data.error) {
      this.errorTarget.textContent = data.response || data.error
      this.errorTarget.classList.remove('is-hidden')
    }
  }

  onSendSuccess({ detail }) {
    const message = this.returnCodeOptionTarget.querySelector('input').checked
      ? 'Unlock code generated'
      : this.statusValue === 'locked'
      ? 'System successfully unlocked'
      : 'System successfully locked'

    showNotification({ type: 'success', message })

    const [, , xhr] = detail
    const result = JSON.parse(xhr.response).request_result

    // if unlock codes were requested, we need to show them
    if (result.unlockCode || result.code1 || result.code2) {
      this.showUnlockCodes(result)
    } else {
      this.statusValue = result.lock_status
      this.operatingTimeValue = result.operating_time

      this.setSerialNumberInput(result.serial_number)
    }

    this.stateValue = 'idle'
  }

  async fetchStatus() {
    this.stateValue = 'loading'
    this.statusValue = undefined
    this.operatingTimeValue = undefined
    this.clearUnlockCodes()

    this.statusFetchTimer = setTimeout(() => {
      if (this.stateValue !== 'loading') return

      this.stateValue = 'unreachable'
    }, 30000)

    try {
      const res = await fetch(`/systems/${this.serialNumberValue}/fetch_lock_status`)
      const result = await res.json()

      if (!res.ok) {
        const error = result.error ? result.error.replaceAll('\n', '<br>') : 'Unknown error'
        throw new Error(error)
      }

      this.stateValue = 'idle'
      this.statusValue = result.lock_status
      this.operatingTimeValue = result.operating_time

      this.setSerialNumberInput(result.serial_number)
    } catch (err) {
      console.error(err)
      this.stateValue = 'unreachable'
    }
  }

  stateValueChanged() {
    switch (this.stateValue) {
      case 'loading':
        this.loaderTarget.classList.remove('is-hidden')
        this.refetchButtonTarget.classList.add('is-hidden')
        this.formTarget.classList.add('is-hidden')
        break
      case 'unreachable':
        this.loaderTarget.classList.add('is-hidden')
        this.refetchButtonTarget.classList.remove('is-hidden')
        this.formTarget.classList.add('is-hidden')
        break
      case 'idle':
        this.loaderTarget.classList.add('is-hidden')
        this.formTarget.classList.remove('is-hidden')
        this.refetchButtonTarget.classList.add('is-hidden')
        break
    }
  }

  statusValueChanged() {
    switch (this.statusValue) {
      case 'locked':
        this.refetchButtonTarget.classList.add('is-hidden')
        this.lockStatusTarget.innerHTML = 'Locked'
        this.lockStatusTarget.classList.remove('has-text-success')
        this.lockStatusTarget.classList.add('has-text-danger')
        this.toggleLockOption(false)
        this.togglePermanentUnlockOption(true)
        this.toggleTemporaryUnlockOption(true)
        this.toggleReturnCodeOption(false)
        this.operatingTimeTarget.classList.add('is-hidden')
        break
      case 'unknown':
        this.refetchButtonTarget.classList.add('is-hidden')
        this.lockStatusTarget.innerHTML = 'Unknown'
        this.lockStatusTarget.classList.remove('has-text-danger')
        this.lockStatusTarget.classList.remove('has-text-success')
        this.toggleLockOption(true)
        this.togglePermanentUnlockOption(true)
        this.toggleTemporaryUnlockOption(true)
        this.toggleReturnCodeOption(false)
        this.operatingTimeTarget.classList.add('is-hidden')

        this.temporaryUnlockOptionTarget.querySelector('input').checked = true
        this.returnCodeOptionTarget.querySelector('input').checked = true
        this.formTarget.querySelector('input[name="system[days]"]').disabled = false
        break
      case 'temporarily_unlocked':
        this.refetchButtonTarget.classList.add('is-hidden')
        this.lockStatusTarget.innerHTML = 'Unlocked'
        this.lockStatusTarget.classList.remove('has-text-danger')
        this.lockStatusTarget.classList.add('has-text-success')
        this.toggleLockOption(true)
        this.togglePermanentUnlockOption(true)
        this.toggleTemporaryUnlockOption(true)
        this.toggleReturnCodeOption(true)
        this.operatingTimeTarget.classList.remove('is-hidden')
        break
      case 'permanently_unlocked':
        this.refetchButtonTarget.classList.add('is-hidden')
        this.lockStatusTarget.innerHTML = 'Unlocked'
        this.lockStatusTarget.classList.remove('has-text-danger')
        this.lockStatusTarget.classList.add('has-text-success')
        this.toggleLockOption(true)
        this.togglePermanentUnlockOption(false)
        this.toggleTemporaryUnlockOption(false)
        this.toggleReturnCodeOption(false)
        this.operatingTimeTarget.classList.add('is-hidden')
        break
    }
  }

  toggleLockOption(enabled = true) {
    this.lockOptionTarget.classList.toggle('is-disabled', !enabled)
    this.lockOptionTarget.querySelector('input').disabled = !enabled
  }

  togglePermanentUnlockOption(enabled = true) {
    this.permanentUnlockOptionTarget.classList.toggle('is-disabled', !enabled)
    this.permanentUnlockOptionTarget.querySelector('input').disabled = !enabled
  }

  toggleTemporaryUnlockOption(enabled = true) {
    this.temporaryUnlockOptionTarget.classList.toggle('is-disabled', !enabled)
    this.temporaryUnlockOptionTarget.querySelector('input').disabled = !enabled
  }

  toggleReturnCodeOption(enabled = true) {
    this.returnCodeOptionTarget.classList.toggle('is-disabled', !enabled)
    this.returnCodeOptionTarget.querySelector('input').disabled = !enabled
    if (!enabled) {
      this.returnCodeOptionTarget.querySelector('input').checked = false
    }
    let hiddenReturnCodeInput = this.formTarget.querySelector('input[name="system[return_code]"]')
    hiddenReturnCodeInput.value = enabled ? '' : 'on'
  }

  operatingTimeValueChanged() {
    if (this.statusValue === 'locked' || this.statusValue === 'unknown') return

    if (this.operatingTimeValue) {
      this.operatingTimeTarget.querySelector('strong').innerHTML = this.operatingTimeValue
    }
  }

  unlockActionChanged({ target }) {
    const enableDaysInput = target.value === 'temporary_unlock'

    this.formTarget.querySelector('input[name="system[days]"]').disabled = !enableDaysInput

    if (this.statusValue !== 'unknown') {
      const lockAction = target.value === 'lock'

      this.toggleReturnCodeOption(!lockAction)
    }
  }

  showUnlockCodes(result) {
    if (result.unlockCode) {
      this.unlockCodesTarget.querySelector('h5').textContent = 'Unlock code'
      this.unlockCodesTarget.querySelector('pre').textContent = result.unlockCode
    } else {
      this.unlockCodesTarget.querySelector('h5').textContent = 'Permanent unlock codes'
      this.unlockCodesTarget.querySelector('pre').textContent = `Code 1: ${result.code1}\nCode 2: ${result.code2}`
    }
    this.unlockCodesTarget.classList.remove('is-hidden')
  }

  clearUnlockCodes() {
    this.unlockCodesTarget.querySelector('h5').textContent = ''
    this.unlockCodesTarget.querySelector('pre').textContent = ''
    this.unlockCodesTarget.classList.add('is-hidden')
  }

  setSerialNumberInput(value) {
    this.formTarget.querySelector('input[name="system[primary_device_serial_number]"]').value = value
  }
}
